import { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { ProductType } from 'types/api/product.type';

import { useAppSelector } from 'redux/hooks';
import { getUserPermissions } from 'redux/slices/userData';

import { productDataService } from 'services/products.service';

import { CatalogueContext, LayoutContext } from 'contexts';

import { useCatalogueLabels, useFetch } from 'hooks';

import { checkParams } from 'helpers/checkParams';
import { formatSeasonLabel } from 'helpers/formatSeason';

import { ListItem } from '../ListItem';

const useProductList = (isGridChangeSet: Function) => {
  const { genre, category } = useParams();
  const { genreLabel, categoryLabel } = useCatalogueLabels();

  const { zone, season, marketLine, warehouse } = useAppSelector((state) => state.settings);
  const permissions = useAppSelector(getUserPermissions);

  const { breadcrumbSet, breadcrumbSecondarySet, grid, additionalInfo, additionalInfoSet } =
    useContext(LayoutContext);
  const { products, productsSet, notifications, isOrderModalVisible, isOrderModalVisibleSet } =
    useContext(CatalogueContext);

  const apiParams = { season, marketLine, genre, category, warehouse: warehouse.code, zone };

  const { content, isLoading } = useFetch(
    checkParams(apiParams) ? () => productDataService.getProducts(apiParams) : undefined,
    [season, marketLine, genre, category, warehouse, zone]
  );

  const productsMapped = products?.map((item: ProductType) => {
    const title = `${item.genderCode} ${item.itemCode} ${item.mainFabricCode}`;

    const desc = `${item.mainFabricCode} ${item.mainFabricDesc}`;

    const notification = notifications?.find(
      (notificationItem) => notificationItem?.itemCode === item.itemCode
    );

    if (!permissions.includes('VIEW_INACTIVE_ITEMS') && !item.active) return null;

    return (
      <ListItem
        data={item}
        key={item.id}
        layout={grid}
        title={title}
        desc={desc}
        notificationCount={notification?.notificationCount}
        notificationUnRead={notification?.status}
      />
    );
  });

  useEffect(() => {
    if (marketLine && season && category) {
      const breadcrumb = [
        {
          label: marketLine,
        },
        {
          label: formatSeasonLabel(season),
        },
        {
          label: categoryLabel(category),
        },
      ];

      breadcrumbSet(breadcrumb);
    }

    if (genre) {
      const breadcrumbSecondary = [
        {
          label: formatSeasonLabel(season),
        },
        {
          label: marketLine,
        },
        {
          label: genreLabel(genre),
        },
        {
          label: category ? categoryLabel(category) : category,
        },
      ];

      breadcrumbSecondarySet(breadcrumbSecondary);
    }
  }, [season, marketLine, category, genre, genreLabel, categoryLabel]);

  useEffect(() => {
    if (!content) return;

    if (!Array.isArray(content)) return;

    const seenIds = new Set<string>();
    const deduplicated = content.filter((item: ProductType) => {
      if (!item?.id) return true;
      if (seenIds.has(item.id)) return false;
      seenIds.add(item.id);
      return true;
    });
    productsSet(deduplicated);
  }, [content]);

  useEffect(() => {
    isGridChangeSet(true);
    isOrderModalVisibleSet(false);

    if (!additionalInfo) return;

    additionalInfoSet([]);
  }, []);

  return {
    productsMapped,
    isLoading,
    grid,
    isOrderModalVisible,
    isOrderModalVisibleSet,
    content,
  };
};

export { useProductList };
